import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, ShieldCheck, CheckCircle2, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { submitLead } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useReveal } from "@/lib/reveal";

const VERTICAL_OPTIONS = [
  { value: "marine", label: "Marine" },
  { value: "boat_club", label: "Boat Club" },
  { value: "equipment", label: "Equipment" },
  { value: "recreation", label: "Recreation" },
  { value: "powersports", label: "Powersports" },
  { value: "tours", label: "Tours & Experiences" },
  { value: "events", label: "Events" },
  { value: "other", label: "Other" },
];
const VERTICAL_VALUES = new Set(VERTICAL_OPTIONS.map((v) => v.value));

const FAQS = [
  { q: "How fast can I get a demo?", a: "Usually same-day. Pick a slot or drop your details and we'll be in touch within a few hours." },
  { q: "Do you offer free trials?", a: "We run paid pilots that get refunded if RentAnything isn't a fit by go-live. That keeps everyone honest." },
  { q: "Can I migrate from another tool?", a: "Yes — inventory, customers, future reservations, and historical bookings come with you. Migration is included." },
  { q: "Where are you located?", a: "Distributed team across North America. Operators on dock, builders on Slack." },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const initial = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  vertical: "",
  units: "",
  message: "",
  company_website: "", // honeypot
  source: "contact_form",
};

// Lazy init: read ?vertical= from the URL on first render so the Select is
// controlled with the correct value from mount (avoids a useEffect race with
// shadcn's Select rendering its placeholder before state hydrates).
function initialForm() {
  if (typeof window === "undefined") return initial;
  try {
    const v = new URLSearchParams(window.location.search).get("vertical");
    if (v && new Set(["marine","boat_club","equipment","recreation","powersports","tours","events","other"]).has(v)) {
      return { ...initial, vertical: v };
    }
  } catch { /* no-op */ }
  return initial;
}

export default function Contact() {
  useSeo({
    title: "Contact · Book a demo",
    description:
      "Book a demo with the RentAnything.ai team or reach our support inbox. 30-minute calls, operator-to-operator.",
    path: "/contact",
    jsonLd: FAQ_JSONLD,
  });
  useReveal();

  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Deep-link support: /contact?vertical=boat_club pre-selects the dropdown.
  // Lazy initialState above handles first mount; this useEffect covers
  // soft-navigation (e.g. clicking the home teaser without a full page load).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const v = searchParams.get("vertical");
    if (v && VERTICAL_VALUES.has(v)) {
      setForm((f) => (f.vertical === v ? f : { ...f, vertical: v }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitLead(form);
      if (res.ok) {
        setDone(true);
        toast.success(res.deduped ? "Thanks — we already have your details on file. Updated." : "Thanks — we'll be in touch shortly.");
        setForm(initial);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Couldn't send right now. Try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="ra-mesh">
        <div className="ra-container py-20 sm:py-28">
          <div className="max-w-2xl">
            <span className="ra-pill"><span className="h-1.5 w-1.5 rounded-full bg-ra-amber" /> Operator-to-operator demos</span>
            <h1 className="mt-5 ra-h1">Let's see if we're a fit.</h1>
            <p className="mt-5 ra-lead">
              30 minutes, your actual rules, no slides. We'll configure RentAnything against your fleet on the call and send pricing same day.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="ra-container grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          {/* FORM */}
          <div className="rounded-3xl border border-ra-line bg-white p-7 sm:p-9 shadow-card reveal" data-testid="contact-form-card">
            {done ? (
              <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="contact-success-state">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                  <CheckCircle2 size={24} />
                </div>
                <h2 className="mt-5 font-display text-2xl font-semibold text-ra-ink">Got it — we'll be in touch.</h2>
                <p className="mt-3 max-w-md text-sm text-ra-ink/70">
                  Someone from the team will reach out within a few hours. If you don't hear back, email us at{" "}
                  <a className="ra-link" href="mailto:inquiry@rentanything.ai">inquiry@rentanything.ai</a>.
                </p>
                <button
                  type="button"
                  onClick={() => setDone(false)}
                  className="ra-btn-ghost mt-7"
                  data-testid="contact-send-another-btn"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                <div>
                  <h2 className="font-display text-xl font-semibold text-ra-ink">Book a demo / Get started</h2>
                  <p className="mt-1 text-sm text-ra-ink/60">Fill this out — we'll reach back same-day.</p>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="name">Your name *</Label>
                    <Input id="name" required value={form.name} onChange={set("name")} placeholder="Sarah Karras" className="mt-1.5" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="business_name">Business name</Label>
                    <Input id="business_name" value={form.business_name} onChange={set("business_name")} placeholder="Lakeside Marina" className="mt-1.5" data-testid="contact-business-input" />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" required type="email" value={form.email} onChange={set("email")} placeholder="sarah@lakesidemarina.com" className="mt-1.5" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone <span className="text-ra-ink/50">(optional)</span></Label>
                    <Input id="phone" value={form.phone} onChange={set("phone")} placeholder="+1 555 0000" className="mt-1.5" data-testid="contact-phone-input" />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="vertical">Rental type / vertical</Label>
                    <Select value={form.vertical} onValueChange={(v) => setForm((f) => ({ ...f, vertical: v }))}>
                      <SelectTrigger id="vertical" className="mt-1.5" data-testid="contact-vertical-select">
                        <SelectValue placeholder="Choose a vertical" />
                      </SelectTrigger>
                      <SelectContent>
                        {VERTICAL_OPTIONS.map((v) => (
                          <SelectItem key={v.value} value={v.value} data-testid={`contact-vertical-${v.value}`}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="units"># of units <span className="text-ra-ink/50">(optional)</span></Label>
                    <Input id="units" value={form.units} onChange={set("units")} placeholder="e.g. 22 boats, 140 SKUs" className="mt-1.5" data-testid="contact-units-input" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">Tell us about your operation</Label>
                  <Textarea id="message" value={form.message} onChange={set("message")} rows={4} placeholder="What do you rent today, what software (if any), what's broken?" className="mt-1.5" data-testid="contact-message-input" />
                </div>

                {/* Honeypot — hidden from humans */}
                <div className="hidden" aria-hidden="true">
                  <label>
                    Website (leave blank)
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.company_website}
                      onChange={set("company_website")}
                      data-testid="contact-honeypot"
                    />
                  </label>
                </div>

                <div className="flex items-center justify-between gap-4 pt-2">
                  <p className="text-xs text-ra-ink/55 flex items-center gap-1.5">
                    <ShieldCheck size={12} className="text-ra-ocean" /> We never share your info.
                  </p>
                  <button type="submit" disabled={submitting} className="ra-btn-primary" data-testid="contact-submit-btn">
                    {submitting ? (<><Loader2 size={16} className="animate-spin" /> Sending</>) : (<>Book my demo <ArrowRight size={16} /></>)}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* SUPPORT */}
          <aside className="space-y-5 reveal">
            <div className="rounded-3xl border border-ra-line bg-ra-paper p-7 shadow-card">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-ra-amber/15 text-ra-amberDeep">
                <Mail size={20} />
              </div>
              <h3 className="mt-5 font-display text-lg font-semibold text-ra-ink">Support for existing customers</h3>
              <p className="mt-2 text-sm text-ra-ink/75">
                Need help with a live account? Email the team directly — most replies come back within a couple of hours during operator hours.
              </p>
              <a href="mailto:inquiry@rentanything.ai" className="ra-link mt-4 inline-flex items-center gap-1 text-sm font-semibold" data-testid="support-email-link">
                inquiry@rentanything.ai <ArrowRight size={14} />
              </a>
            </div>

            <div className="rounded-3xl border border-ra-line bg-white p-7 shadow-card">
              <h3 className="font-display text-base font-semibold text-ra-ink">What happens after I submit?</h3>
              <ol className="mt-4 space-y-3 text-sm text-ra-ink/75">
                {[
                  "We reach out within a few hours to confirm a time.",
                  "30-minute call with someone who has actually run a rental business.",
                  "We configure RentAnything to your fleet during the call.",
                  "You get pricing in writing same day. No follow-up sales squeeze.",
                ].map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ra-ocean text-[10px] font-bold text-white">{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="ra-section bg-ra-paper border-y border-ra-line">
        <div className="ra-container max-w-3xl">
          <div className="text-center reveal">
            <span className="ra-eyebrow">Common questions</span>
            <h2 className="mt-3 ra-h2">Answers before you ask.</h2>
          </div>
          <Accordion type="single" collapsible className="mt-10 w-full" data-testid="contact-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="border-ra-line">
                <AccordionTrigger className="text-left text-base font-semibold text-ra-ink hover:text-ra-ocean">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-ra-ink/75">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
}
